import { Grid, useMediaQuery } from "@mui/material";
import CloseIconButton from "components/AppModalDialogsV2/Dialogs/lib/CloseIconButton";
import useCalculateScale from "components/PopUpManager/hooks/useCalculateScale";
import { useWebStoreContext } from "contexts";
import { useGoogleAnalyticsContext } from "contexts/GoogleAnalyticsProvider";
import { IWebStoreGeneralErrorResponse } from "contexts/WebStoreProvider/types";
import { deepLinkAssets } from "assets/cdn";
import { useAppDispatch, useAppSelector, useImagePath } from "hooks";
import useBreakpoints from "hooks/useBreakpoints";
import useRewardsById from "hooks/useRewardsById";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
	onClosePopUp,
	onIsLPAnimationPlaying,
	onScrollToShopSection,
	selectCommonState
} from "redux/slices";
import {
	Button,
	Container,
	Description,
	RewardContainer,
	RewardNumber,
	Title
} from "./styles";
import { useLinks } from "hooks/useLinks";

const errorCodes = {
	used: "deeplink_already_used",
	inactive: "inactive_deeplink",
	maxCountReached: "deeplink_global_count_reached"
};

interface RewardingDeeplinksProps {}

export const RewardingDeeplinks: React.FC<RewardingDeeplinksProps> = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { isSmDown } = useBreakpoints();
	const { getImage } = useImagePath("cdn");
	const rewardingAssets = deepLinkAssets.rewarding;
	const successBackground = getImage(rewardingAssets.success.portrait);
	const successBackgroundLandscape = getImage(
		rewardingAssets.success.landscape
	);
	const alreadyClaimedBackground = getImage(
		rewardingAssets.alreadyClaimed.portrait
	);
	const alreadyClaimedBackgroundLandscape = getImage(
		rewardingAssets.alreadyClaimed.landscape
	);
	const errorBackground = getImage(rewardingAssets.error.portrait);
	const errorBackgroundLandscape = getImage(rewardingAssets.error.landscape);
	const { syncUserTracker } = useWebStoreContext();
	const { reportEvent } = useGoogleAnalyticsContext();
	const { deeplinkState } = useAppSelector(selectCommonState);
	const isPortrait = useMediaQuery("(orientation: portrait)");
	const { isError, isSuccess, error, data } = deeplinkState;
	const displayRewards = useRewardsById(data?.rewardId as string);
	const generalError = error as IWebStoreGeneralErrorResponse;
	const { errorCode } = generalError?.data || {};
	const scale = useCalculateScale(isSmDown ? 443 : 945, isSmDown ? 712 : 360);
	const links = useLinks();

	const onClose = () => {
		dispatch(onClosePopUp());
		navigate(links.shop.path);
		dispatch(onScrollToShopSection("Featured Deals"));
		syncUserTracker();
	};

	useEffect(() => {
		reportEvent("dialog_shown", {
			source: "rewarding_deeplinks"
		});
	}, []);

	useEffect(() => {
		if (isSuccess && displayRewards?.[0]?.id === "loyalty_points") {
			dispatch(onIsLPAnimationPlaying(true));
		}
	}, [isSuccess, displayRewards?.[0]?.id]);

	return (
		<Container
			scale={scale}
			background={
				isSmDown
					? isSuccess
						? successBackground
						: errorCode === errorCodes.used
						? alreadyClaimedBackground
						: errorBackground
					: isSuccess
					? successBackgroundLandscape
					: errorCode === errorCodes.used
					? alreadyClaimedBackgroundLandscape
					: errorBackgroundLandscape
			}
		>
			{!isSuccess && (
				<CloseIconButton onClose={() => dispatch(onClosePopUp())} />
			)}
			<Grid
				container
				justifyContent={{
					xs: isPortrait ? "center" : "flex-end",
					sm: "flex-end"
				}}
			>
				<Grid
					item
					xs={isPortrait ? 12 : isSuccess ? 7 : 6}
					sm={isSuccess ? 7 : 6}
					display="flex"
					justifyContent="center"
				>
					<Grid
						item
						position="absolute"
						top={
							isSuccess && isSmDown
								? "10%"
								: isSuccess && !isSmDown
								? "5%"
								: errorCode === errorCodes.used
								? isSmDown
									? "15%"
									: "20%"
								: "30%"
						}
						className="sub-container"
					>
						<Title isError={isError} style={{ paintOrder: "stroke fill" }}>
							{isSuccess ? (
								<>
									<div>You got</div>
									<div>
										{Number(displayRewards?.[0]?.description) > 1
											? displayRewards?.[0]?.pluralLabel
											: displayRewards?.[0]?.singularLabel}
										!
									</div>
								</>
							) : errorCode === errorCodes.used ? (
								"Reward Claimed!"
							) : errorCode === errorCodes.inactive ||
							  errorCode === errorCodes.maxCountReached ? (
								"Expired Gift"
							) : (
								"Sorry"
							)}
						</Title>
						<Description
							isError={isError}
							style={{ paintOrder: "stroke fill" }}
						>
							{isSuccess
								? "Your free reward is now in your balance."
								: errorCode === errorCodes.used
								? "You’ve already claimed this reward - stay tuned for the next bonus!"
								: errorCode === errorCodes.inactive ||
								  errorCode === errorCodes.maxCountReached
								? "Looks like this one’s gone - don’t miss the next!"
								: "You are not eligible to collect this bonus."}
						</Description>
						{isSuccess && (
							<RewardContainer>
								<img
									src={displayRewards?.[0]?.imageUrl}
									alt={displayRewards?.[0]?.singularLabel}
									width={"auto"}
									height={"100%"}
								/>
								<RewardNumber
									ammount={displayRewards?.[0]?.description}
									style={{ paintOrder: "stroke fill" }}
								>
									{displayRewards?.[0]?.description}
								</RewardNumber>
							</RewardContainer>
						)}
					</Grid>
					<Grid container>
						<Grid item xs={12} display="flex" justifyContent="center">
							<Button
								onClick={onClose}
								themeColor={isSuccess ? "primary" : "secondary"}
								sx={{
									minWidth: {
										xs: isPortrait ? "80%" : "40%",
										sm: "40%"
									},
									position: "absolute",
									bottom: isSmDown ? "2rem" : "1rem"
								}}
							>
								{isSuccess ? "Claim Reward" : "Continue Shopping"}
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Container>
	);
};
